import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule, UrlTree } from '@angular/router';
import { take, tap } from 'rxjs';
import { ApiService } from 'shared/src/services/api.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { getDeviceType } from 'shared/src/utils/device.util';

@Component({
  selector: 'app-download',
  imports: [MatButtonModule, RouterModule],
  templateUrl: './app-download.component.html',
  styleUrl: './app-download.component.scss',
})
export class AppDownloadComponent implements OnInit {
  private readonly adaptyBaseUrl =
    'https://api-ua.adapty.io/api/v1/attribution/click?adpt_cid=ODY6MTEwOjE3OA&channel=web-funnel';
  private readonly iosAppStoreUrl =
    'https://apps.apple.com/us/app/peech-text-to-speech-reader/id1429704005';
  private readonly androidPlayStoreUrl =
    'https://play.google.com/store/apps/details?id=com.peech.android';
  /**
   * When the buyer is already signed in on the web (the app's own paywall), the store is an
   * option rather than the only way forward — this is where to continue to. A parsed tree,
   * not a string: the return path carries the library's own query string, which routerLink
   * would otherwise escape into the path. Unset for funnel traffic, which has no session to
   * return to.
   */
  @Input() continueUrl: UrlTree | undefined;

  iosUrl: string = this.iosAppStoreUrl;
  androidUrl: string = this.androidPlayStoreUrl;
  showIosButton = true;
  showAndroidButton = true;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private apiService: ApiService,
    private userInfo: UserInfoService
  ) {}

  ngOnInit(): void {
    const email = this.userInfo.getEmail();
    const stripeCustomerId = this.userInfo.getStripeCustomerId();
    const deviceType = getDeviceType();

    this.showIosButton = deviceType !== 'android';
    this.showAndroidButton = deviceType !== 'ios';

    this.apiService
      .getAppstoreDeepLink(email, stripeCustomerId, this.adaptyBaseUrl)
      .pipe(
        take(1),
        tap(response => {
          this.iosUrl = this.buildAdaptyUrl('ios', response.jwtToken, email);
          this.androidUrl = this.buildAdaptyUrl('android', response.jwtToken, email);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private buildAdaptyUrl(
    platform: 'ios' | 'android',
    token: string,
    email: string | undefined
  ): string {
    const url = new URL(this.adaptyBaseUrl);
    const deferredData = {
      action: 'login',
      token,
      email: email ?? '',
    };

    url.searchParams.set(`${platform}_deferred_data`, JSON.stringify(deferredData));

    return url.toString();
  }
}
